import React from 'react';
import { motion } from 'framer-motion';
import * as LuIcons from 'react-icons/lu';
const { LuStar, LuStore, LuArrowLeft, LuExternalLink, LuShieldCheck } = LuIcons;
import SafeIcon from '../common/SafeIcon';

export default function AnalysisDashboard({ onNewSearch, data }) {
  // Destructure real product data with fallbacks
  const {
    title = "Product Title",
    image = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=400&q=80",
    rating = "N/A",
    reviews = 0,
    availability = "Check site for availability",
    brand = "Unknown Brand",
    url = "#"
  } = data;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Sub-header / Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-[90px] md:top-[100px] z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button 
            onClick={onNewSearch} 
            className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <SafeIcon icon={LuArrowLeft} /> Back to Search
          </button>
          <div className="hidden sm:flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
            <SafeIcon icon={LuShieldCheck} className="text-green-500" /> Verified Product Data
          </div>
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="container mx-auto px-4 md:px-6 py-8"
      >
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-gray-100 overflow-hidden">
            <div className="md:flex">
              {/* Product Image Section */}
              <div className="md:w-2/5 bg-gray-50 p-8 flex items-center justify-center border-b md:border-b-0 md:border-r border-gray-100">
                <div className="relative group">
                  <img 
                    src={image} 
                    alt={title} 
                    className="max-w-full h-auto rounded-xl shadow-lg transition-transform group-hover:scale-105 duration-300"
                  />
                </div>
              </div>

              {/* Product Info Section */}
              <div className="md:w-3/5 p-8 md:p-12">
                <div className="flex items-center gap-2 text-blue-600 font-bold text-xs uppercase tracking-widest mb-4">
                  <SafeIcon icon={LuStore} /> {brand}
                </div>
                
                <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
                  {title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 mb-8">
                  <div className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-full font-bold border border-yellow-100">
                    <SafeIcon icon={LuStar} className="fill-yellow-400 text-yellow-500" />
                    {rating}
                  </div>
                  <div className="text-gray-500 font-medium whitespace-nowrap">
                    {typeof reviews === 'number' ? reviews.toLocaleString() : reviews} Reviews
                  </div>
                  <div className={`px-3 py-1.5 rounded-full font-bold text-xs uppercase tracking-wider ${
                    availability?.toLowerCase().includes('in stock') 
                      ? 'bg-green-50 text-green-700 border border-green-100' 
                      : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`}>
                    {availability}
                  </div>
                </div>

                <div className="space-y-6 pt-6 border-t border-gray-100">
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-blue-500/25 transform hover:-translate-y-1"
                  >
                    View on Amazon <SafeIcon icon={LuExternalLink} />
                  </a>
                  
                  <p className="text-center text-xs text-gray-400 font-medium">
                    Prices and availability are accurate as of the time of analysis.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Simple Info Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                <SafeIcon icon={LuShieldCheck} className="text-xl" />
              </div>
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase">Verified</div>
                <div className="text-sm font-bold text-gray-900">Direct Data</div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <SafeIcon icon={LuStore} className="text-xl" />
              </div>
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase">Source</div>
                <div className="text-sm font-bold text-gray-900">Amazon Marketplace</div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center text-green-600">
                <SafeIcon icon={LuExternalLink} className="text-xl" />
              </div>
              <div>
                <div className="text-xs font-bold text-gray-400 uppercase">Status</div>
                <div className="text-sm font-bold text-gray-900">Live Connection</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}